import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import Seo from "../components/Seo";
import CTASection from "../components/home/CTASection";
import useReveal from "../hooks/useReveal";
import api, { imageUrl } from "../lib/api";
import { ArrowLeft, ArrowRight, Clock, Tag } from "lucide-react";

const CAT_RELATED = {
  "planning-and-contractors": { label: "Explore Our Services", to: "/services" },
  "cost-and-financing": { label: "Request a Free Estimate", to: "/contact" },
  "basement-development": { label: "Basement Development", to: "/services/basement" },
  "legal-basement-suites": { label: "Legal Basement Suites", to: "/services/legal-suites" },
  "kitchen-renovations": { label: "Kitchen Renovations", to: "/services/kitchen" },
  "bathroom-renovations": { label: "Bathroom Renovations", to: "/services/bathroom" },
  "whole-home-and-additions": { label: "Explore Our Services", to: "/services" },
  "permits-codes-inspections": { label: "Explore Our Services", to: "/services" },
  "commercial-renovations": { label: "Commercial Renovations", to: "/commercial" },
  "climate-and-local": { label: "Explore Our Services", to: "/services" },
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  useReveal([post]);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        api.get(`/blog?category=${r.data.category}`).then((res) => {
          setRelated(res.data.filter((p) => p.slug !== slug).slice(0, 3));
        }).catch(() => {});
      })
      .catch(() => setNotFound(true));
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-display text-3xl font-bold text-[#0f2740] mb-3">Article not found</h1>
        <p className="text-[#6b7683] mb-6">This article may have moved or been removed.</p>
        <Link to="/blog" className="px-6 py-3 rounded-full bg-[#2f9fe6] text-white font-semibold">Back to Blog</Link>
      </div>
    );
  }

  if (!post) return <div className="min-h-[70vh] flex items-center justify-center text-[#6b7683]">Loading…</div>;

  const cta = CAT_RELATED[post.category] || { label: "Request a Free Estimate", to: "/contact" };
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: imageUrl(post.cover),
    author: { "@type": "Organization", name: "Classic Interior Renovations LTD" },
    publisher: { "@type": "Organization", name: "Classic Interior Renovations LTD" },
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    mainEntityOfPage: `https://classic-interior-renovations.com/blog/${post.slug}`,
  };

  return (
    <>
      <Seo title={post.title} path={`/blog/${post.slug}`} description={post.excerpt} image={imageUrl(post.cover)} />
      <Helmet><script type="application/ld+json">{JSON.stringify(articleSchema)}</script></Helmet>

      {/* HERO */}
      <section className="relative pt-36 pb-20 bg-[#0f2740] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20"><img src={imageUrl(post.cover)} alt="" className="w-full h-full object-cover" /></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740] via-[#0f2740]/85 to-[#0f2740]/60" />
        <div className="container-x relative z-10 max-w-3xl">
          <button onClick={() => navigate("/blog")} className="inline-flex items-center gap-2 text-[#a9c6dd] hover:text-white mb-6 transition-colors" data-testid="back-to-blog"><ArrowLeft className="w-4 h-4" /> Back to Blog</button>
          <h1 className="font-display text-3xl sm:text-5xl font-bold leading-tight mb-5">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-[#a9c6dd]">
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.read_time} min read</span>
            <span>·</span>
            <span>{post.author}</span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="py-16 bg-white">
        <div className="container-x grid lg:grid-cols-[1fr_320px] gap-12 max-w-6xl">
          <article className="blog-body reveal" data-testid="blog-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body || "") }} />
          <aside className="reveal">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="bg-[#0f2740] rounded-2xl p-7 text-white">
                <h3 className="font-display text-xl font-bold mb-2">Planning a project?</h3>
                <p className="text-[#a9c6dd] text-sm mb-5">Get expert help from an experienced Edmonton renovation team.</p>
                <Link to={cta.to} className="inline-flex items-center gap-2 w-full justify-center px-5 py-3 rounded-full bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors" data-testid="sidebar-cta">{cta.label} <ArrowRight className="w-4 h-4" /></Link>
              </div>
              {post.tags && post.tags.length > 0 && (
                <div className="bg-[#f7f9fb] rounded-2xl p-6 border border-[#eef1f4]">
                  <h4 className="font-semibold text-[#0f2740] mb-3 flex items-center gap-2 text-sm"><Tag className="w-4 h-4 text-[#2f9fe6]" /> Topics</h4>
                  <div className="flex flex-wrap gap-2">{post.tags.map((t) => <span key={t} className="px-3 py-1 rounded-full bg-white border border-[#e2e6ea] text-[#4a5560] text-xs">{t}</span>)}</div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-16 bg-[#f7f9fb]">
          <div className="container-x max-w-6xl">
            <h2 className="font-display text-2xl font-bold text-[#0f2740] mb-8">Related articles</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`related-${i}`} className="group bg-white rounded-2xl overflow-hidden border border-[#eef1f4] hover:shadow-lg transition-all">
                  <div className="h-40 overflow-hidden"><img src={imageUrl(p.cover)} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold text-[#0f2740] text-sm leading-snug group-hover:text-[#2f9fe6] transition-colors">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </>
  );
}
